/**
 * The Leadership Brief tab (GUS's Atlas "executive" view): its node, its
 * defaults, and the values GUS would silently ignore, found before Save.
 *
 * GUS reads `features.modules.atlas.tabs.executive`. The internal key stays
 * "executive"; only the label people see is "Leadership Brief". Unlike every
 * other Atlas tab the switch is OFF when absent: the tab shows only where an
 * admin turns it on. That is GUS's rule from the Leadership Brief release; an
 * earlier GUS build reads an absent switch as on, which the editor warns about
 * when the tenant's flag manifest says so.
 *
 * GUS drops a malformed setting, at most logs it, and carries on with its
 * default, so nobody is told. The backend's check
 * (backend/app/services/executive_settings.py) is what refuses the write; this
 * copy lets the editor say what is wrong while the admin is still editing. Both
 * run against one table of cases (executiveSettings.cases.json), rule id for
 * rule id, so they cannot drift apart.
 *
 * Absent means "use GUS's default" for every key here, so absence is never
 * refused. What the catalogue says a project can serve is not judged here: it
 * changes with the data, so the editor shows it as a warning, not a refusal.
 */
import { Bilingual, SettingError, pyStrip } from '../satisfaction/satisfactionSettings';

export const EXECUTIVE_PATH = 'modules.atlas.tabs.executive';
export const EXECUTIVE_CONFIG = `${EXECUTIVE_PATH}.config`;

/** What people see when no label override is set. */
export const DEFAULT_LABEL: Bilingual = { en: 'Leadership Brief', ar: 'الموجز القيادي' };

/** GUS constants.py DASHBOARDS, in GUS's order, then a person's own starting list. */
export const DASHBOARDS = [
  'satisfaction', 'city_insights', 'urban_pulse', 'spatial_economics', 'vulnerability', 'community',
  'urban_intelligence',
] as const;
export const MY_KPIS = 'my_kpis';
export const STARTING_SET_KEYS: readonly string[] = [...DASHBOARDS, MY_KPIS];

export const DASHBOARD_LABELS: Readonly<Record<string, Bilingual>> = {
  satisfaction: { en: 'Satisfaction', ar: 'رضا المدينة' },
  city_insights: { en: 'City Insights', ar: 'رؤى المدينة' },
  urban_pulse: { en: 'Urban Pulse', ar: 'نبض المدينة' },
  spatial_economics: { en: 'Spatial Economics', ar: 'الاقتصاد المكاني' },
  vulnerability: { en: 'Vulnerability', ar: 'الهشاشة' },
  community: { en: 'Community', ar: 'المجتمع' },
  urban_intelligence: { en: 'Urban Intelligence', ar: 'الذكاء الحضري' },
  my_kpis: { en: 'My KPIs (each person’s starting list)', ar: 'مؤشراتي (القائمة الأولى لكل شخص)' },
};

/** GUS constants.py FIXED_BASELINE_DEFAULT. */
export const DEFAULT_FIXED_BASELINES: readonly string[] = [
  'jobs:', 'isic:', 'industry:', 'surface:snapshot.job', 'surface:snapshot.productivity',
  'surface:snapshot.gdp_density',
];

/** GUS constants.py MAX_KPIS: a list is cut to this many. */
export const MAX_KPIS = 40;

/** GUS kpi_ref.py RANK. */
export const RANK = /^rank:(high|low):(.+)$/;

/** The keys the editor writes under the tab node, beside the manifest's own leaves. */
export const PANEL_KEYS = ['ai_written', 'config'];

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);
const present = (v: unknown) => v !== undefined && v !== null;

/** The tab node as stored, or undefined. */
export function executiveNode(featureFlags: unknown): unknown {
  let node: unknown = featureFlags;
  for (const part of EXECUTIVE_PATH.split('.')) {
    if (!isPlainObject(node)) return undefined;
    node = node[part];
  }
  return node;
}

/**
 * Whether GUS shows the tab: OFF when the key is absent. A boolean node is the
 * switch itself; an object node is on only when its `enabled` is true.
 */
export function executiveTabOn(featureFlags: unknown): boolean {
  const node = executiveNode(featureFlags);
  if (typeof node === 'boolean') return node;
  return isPlainObject(node) && node.enabled === true;
}

/** Whether the tab may carry AI-written sentences: ON when absent. */
export function aiWrittenOn(featureFlags: unknown): boolean {
  const node = executiveNode(featureFlags);
  return !(isPlainObject(node) && node.ai_written === false);
}

/** One starting-set item as the editor holds it. */
export interface StartingItem {
  measure: string;
  level: string | null;
}

/** A stored item read as (measure, level); null when GUS cannot read it at all. */
export function readItem(raw: unknown): StartingItem | null {
  if (typeof raw === 'string') return { measure: raw, level: null };
  if (isPlainObject(raw) && typeof raw.measure === 'string') {
    return { measure: raw.measure, level: typeof raw.level === 'string' ? raw.level : null };
  }
  return null;
}

/** The stored form of an item: a bare id, or {measure, level} for a rank item with a level. */
export function writeItem(item: StartingItem): string | { measure: string; level: string } {
  return item.level && RANK.test(item.measure) ? { measure: item.measure, level: item.level } : item.measure;
}

// =============================================================================
// CHECKS
// =============================================================================

const M = {
  tab_shape: {
    en: 'The tab is a switch (true or false) or an object of its settings.',
    ar: 'التبويب مفتاح (true أو false) أو كائن يضم إعداداته.',
  },
  enabled_boolean: {
    en: 'The switch is on or off (true or false).',
    ar: 'المفتاح إما مفعّل أو غير مفعّل (true أو false).',
  },
  order_integer: {
    en: 'The position is a whole number.',
    ar: 'الترتيب عدد صحيح.',
  },
  label_text: {
    en: 'A label is text. Leave it empty to use the default label.',
    ar: 'التسمية نص. اتركها فارغة لاستخدام التسمية الافتراضية.',
  },
  ai_written_boolean: {
    en: 'AI-written text is on or off (true or false).',
    ar: 'النص المكتوب بالذكاء الاصطناعي إما مفعّل أو غير مفعّل (true أو false).',
  },
  config_object: {
    en: 'The settings are an object of named settings.',
    ar: 'الإعدادات كائن من إعدادات مسمّاة.',
  },
  starting_sets_object: {
    en: 'The starting sets are an object keyed by dashboard.',
    ar: 'القوائم الأولى كائن مفاتيحه أسماء لوحات المعلومات.',
  },
  starting_sets_dashboard: {
    en: `GUS knows no such dashboard and ignores this list. Use one of: ${STARTING_SET_KEYS.join(', ')}.`,
    ar: `لا يعرف GUS لوحة معلومات بهذا الاسم ويتجاهل هذه القائمة. استخدم إحدى: ${STARTING_SET_KEYS.join(', ')}.`,
  },
  starting_set_list: {
    en: 'A starting set is a list of measures.',
    ar: 'القائمة الأولى قائمة من المقاييس.',
  },
  starting_set_empty: {
    en: 'An empty list means GUS’s default. Remove the list instead.',
    ar: 'القائمة الفارغة تعني الإعداد الافتراضي في GUS. احذف القائمة بدلًا من ذلك.',
  },
  starting_set_item: {
    en: 'Each item is a catalogue measure id, or {"measure": id, "level": unit level}. The portal accepts only these forms.',
    ar: 'كل عنصر معرّف مقياس من الكتالوج، أو {"measure": معرّف, "level": مستوى الوحدة}. لا تقبل البوابة غير هذين الشكلين.',
  },
  starting_set_rank_form: {
    en: 'A rank item reads rank:high:<measure> or rank:low:<measure>.',
    ar: 'عنصر الترتيب يُكتب rank:high:<المقياس> أو rank:low:<المقياس>.',
  },
  starting_set_level_rank_only: {
    en: 'Only a rank item uses a level. GUS reads this one for the whole place and ignores the level.',
    ar: 'لا يستخدم المستوى إلا عنصر الترتيب. يقرأ GUS هذا العنصر للمكان كله ويتجاهل المستوى.',
  },
  starting_set_duplicate: {
    en: 'This measure is listed twice. GUS keeps the first.',
    ar: 'هذا المقياس مذكور مرتين. يُبقي GUS الأول.',
  },
  starting_set_too_many: {
    en: `At most ${MAX_KPIS} measures. GUS drops the rest.`,
    ar: `${MAX_KPIS} مقياسًا على الأكثر. يُسقط GUS الباقي.`,
  },
  fixed_baselines_list: {
    en: 'A list of measure-id prefixes, none empty or blank. GUS sets aside a list that is not, and uses its default.',
    ar: 'قائمة من بادئات معرّفات المقاييس، لا فارغ فيها ولا مسافات فقط. يتجاهل GUS القائمة إن لم تكن كذلك ويستخدم قيمته الافتراضية.',
  },
  fixed_baselines_empty: {
    en: 'An empty list means GUS’s default. Remove the list instead.',
    ar: 'القائمة الفارغة تعني الإعداد الافتراضي في GUS. احذف القائمة بدلًا من ذلك.',
  },
  fixed_baselines_duplicate: {
    en: 'This prefix is listed twice.',
    ar: 'هذه البادئة مذكورة مرتين.',
  },
  deck_name_object: {
    en: 'The report’s project name is {"en": text, "ar": text}.',
    ar: 'اسم المشروع في التقرير هو {"en": نص, "ar": نص}.',
  },
  deck_name_text: {
    en: 'A name is text. Leave it empty to print the place’s own name.',
    ar: 'الاسم نص. اتركه فارغًا لطباعة اسم المكان نفسه.',
  },
};

type Rule = keyof typeof M;

const blank = (v: unknown) => typeof v === 'string' && pyStrip(v) === '';

function itemErrors(raw: unknown, path: string, push: (path: string, rule: Rule) => void): StartingItem | null {
  if (typeof raw === 'string') {
    if (blank(raw)) {
      push(path, 'starting_set_item');
      return null;
    }
    if (raw.startsWith('rank:') && !RANK.test(raw)) push(path, 'starting_set_rank_form');
    return { measure: raw, level: null };
  }
  if (!isPlainObject(raw) || typeof raw.measure !== 'string' || blank(raw.measure)
    || Object.keys(raw).some((k) => k !== 'measure' && k !== 'level')
    || (present(raw.level) && (typeof raw.level !== 'string' || blank(raw.level)))) {
    push(path, 'starting_set_item');
    return null;
  }
  const measure = raw.measure;
  if (measure.startsWith('rank:') && !RANK.test(measure)) push(path, 'starting_set_rank_form');
  const level = typeof raw.level === 'string' ? raw.level : null;
  // A malformed rank item is already refused for its form; its level is not a second fault.
  if (level !== null && !measure.startsWith('rank:')) push(`${path}.level`, 'starting_set_level_rank_only');
  return { measure, level };
}

function configErrors(cfg: Record<string, unknown>, push: (path: string, rule: Rule) => void): void {
  const sets = cfg.starting_sets;
  const setsPath = `${EXECUTIVE_CONFIG}.starting_sets`;
  if (present(sets) && !isPlainObject(sets)) {
    push(setsPath, 'starting_sets_object');
  } else if (isPlainObject(sets)) {
    for (const [dashboard, list] of Object.entries(sets)) {
      const here = `${setsPath}.${dashboard}`;
      if (!STARTING_SET_KEYS.includes(dashboard)) {
        push(here, 'starting_sets_dashboard');
        continue;
      }
      if (!present(list)) continue;
      if (!Array.isArray(list)) {
        push(here, 'starting_set_list');
        continue;
      }
      if (list.length === 0) push(here, 'starting_set_empty');
      if (list.length > MAX_KPIS) push(here, 'starting_set_too_many');
      const seen = new Set<string>();
      list.forEach((raw, i) => {
        const item = itemErrors(raw, `${here}.${i}`, push);
        if (!item) return;
        // A level counts only on a rank item, so two whole-place items differing in level are one item.
        const key = `${item.measure}\u0000${RANK.test(item.measure) ? item.level ?? '' : ''}`;
        if (seen.has(key)) push(`${here}.${i}`, 'starting_set_duplicate');
        seen.add(key);
      });
    }
  }

  const fb = cfg.fixed_baselines;
  const fbPath = `${EXECUTIVE_CONFIG}.fixed_baselines`;
  if (present(fb)) {
    if (!Array.isArray(fb) || fb.some((x) => typeof x !== 'string' || blank(x))) {
      push(fbPath, 'fixed_baselines_list');
    } else if (fb.length === 0) {
      push(fbPath, 'fixed_baselines_empty');
    } else if (new Set(fb).size !== fb.length) {
      push(fbPath, 'fixed_baselines_duplicate');
    }
  }

  const name = cfg.deck_project_name;
  const namePath = `${EXECUTIVE_CONFIG}.deck_project_name`;
  if (present(name)) {
    if (!isPlainObject(name) || Object.keys(name).some((k) => k !== 'en' && k !== 'ar')) {
      push(namePath, 'deck_name_object');
    } else {
      for (const lang of ['en', 'ar'] as const) {
        const v = name[lang];
        if (present(v) && (typeof v !== 'string' || blank(v))) push(`${namePath}.${lang}`, 'deck_name_text');
      }
    }
  }
}

/** Every refused value under the Leadership Brief tab node of a whole `feature_flags` object. */
export function executiveErrors(featureFlags: unknown): SettingError[] {
  const out: SettingError[] = [];
  const push = (path: string, rule: Rule) => out.push({ path, rule, message: M[rule] });
  const node = executiveNode(featureFlags);
  if (!present(node) || typeof node === 'boolean') return out;
  if (!isPlainObject(node)) {
    push(EXECUTIVE_PATH, 'tab_shape');
    return out;
  }
  if (present(node.enabled) && typeof node.enabled !== 'boolean') push(`${EXECUTIVE_PATH}.enabled`, 'enabled_boolean');
  if (present(node.order) && !(typeof node.order === 'number' && Number.isInteger(node.order))) {
    push(`${EXECUTIVE_PATH}.order`, 'order_integer');
  }
  for (const key of ['label', 'label_ar']) {
    const v = node[key];
    if (present(v) && (typeof v !== 'string' || blank(v))) push(`${EXECUTIVE_PATH}.${key}`, 'label_text');
  }
  if (present(node.ai_written) && typeof node.ai_written !== 'boolean') {
    push(`${EXECUTIVE_PATH}.ai_written`, 'ai_written_boolean');
  }
  if (present(node.config) && !isPlainObject(node.config)) {
    push(EXECUTIVE_CONFIG, 'config_object');
  } else if (isPlainObject(node.config)) {
    configErrors(node.config, push);
  }
  return out;
}

/** JSON with object keys sorted, so two readings of one value compare equal whatever their key order. */
const canonical = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  if (isPlainObject(v)) {
    return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v) ?? 'undefined';
};

function valueAt(flags: unknown, path: string): string {
  let node: unknown = flags;
  for (const part of path.split('.')) {
    if (Array.isArray(node) && /^\d+$/.test(part)) {
      node = node[Number(part)];
      continue;
    }
    if (!isPlainObject(node) || !(part in node)) return '<absent>';
    node = node[part];
  }
  return canonical(node);
}

/**
 * The refused values in `after` that `before` (what is stored now) does not
 * already hold, matched as the backend matches them: a value already stored and
 * left exactly as it was is shown in red but never blocks an unrelated edit.
 */
export function introducedExecutiveErrors(after: unknown, before: unknown): SettingError[] {
  const key = (e: SettingError, flags: unknown) => `${e.path}\u0000${e.rule}\u0000${valueAt(flags, e.path)}`;
  const held = new Set(executiveErrors(before).map((e) => key(e, before)));
  return executiveErrors(after).filter((e) => !held.has(key(e, after)));
}

// =============================================================================
// WHAT THIS PROJECT CAN SERVE (from GUS's catalogue; a warning, never a refusal)
// =============================================================================

/** The parts of GUS's catalogue the editor reads (services/api.ts ExecutiveCatalogue). */
export interface CatalogueMeasure {
  id: string;
  label: { en: string | null; ar: string | null };
  dashboard: string;
  city: { ok: boolean; reason_key: string | null };
  rank_levels: string[];
}

export interface CatalogueView {
  coarsest: string | null;
  partial: boolean;
  domain_off: string[];
  groups: Array<{ measures: CatalogueMeasure[] }>;
}

export type Serving =
  | { state: 'ok'; level: string | null }
  | { state: 'unknown'; why: Bilingual }
  | { state: 'cannot'; why: Bilingual };

export function catalogueIndex(cat: CatalogueView): Map<string, CatalogueMeasure> {
  const index = new Map<string, CatalogueMeasure>();
  for (const g of cat.groups) for (const m of g.measures) index.set(m.id, m);
  return index;
}

/**
 * Whether GUS can serve this item for this project, by GUS's own rule
 * (starting_sets.py `_usable`): a rank item needs its base measure and a level
 * (its own, else the coarsest) among that measure's rank levels; any other item
 * needs a whole-place reading. A partial catalogue cannot prove absence.
 */
export function servability(item: StartingItem, cat: CatalogueView, index = catalogueIndex(cat)): Serving {
  const rank = RANK.exec(item.measure);
  const base = rank ? rank[2] : item.measure;
  const entry = index.get(base);
  if (!entry) {
    if (cat.domain_off.includes(base)) {
      return { state: 'cannot', why: {
        en: 'Its area is switched off for this project.',
        ar: 'مجاله معطّل لهذا المشروع.' } };
    }
    if (cat.partial) {
      return { state: 'unknown', why: {
        en: 'Not in the catalogue, but GUS sent only part of it, so this is not checked.',
        ar: 'ليس في الكتالوج، لكن GUS أرسل جزءًا منه فقط، فلم يُتحقق من ذلك.' } };
    }
    return { state: 'cannot', why: {
      en: 'Not in this project’s catalogue. GUS drops it.',
      ar: 'ليس في كتالوج هذا المشروع. يُسقطه GUS.' } };
  }
  if (rank) {
    const level = item.level ?? cat.coarsest;
    if (!level || !entry.rank_levels.includes(level)) {
      return { state: 'cannot', why: {
        en: `Cannot be ranked at ${level ?? 'any level'} here. GUS drops it.`,
        ar: `لا يمكن ترتيبه على مستوى ${level ?? 'أي مستوى'} هنا. يُسقطه GUS.` } };
    }
    return { state: 'ok', level };
  }
  if (!entry.city.ok) {
    const reason = entry.city.reason_key ? ` (${entry.city.reason_key})` : '';
    return { state: 'cannot', why: {
      en: `No whole-place reading here${reason}. GUS drops it.`,
      ar: `لا توجد قراءة للمكان كله هنا${reason}. يُسقطه GUS.` } };
  }
  return { state: 'ok', level: null };
}

/** A catalogue label for an admin's list: GUS's {people} / {People} placeholders read as a plain word. */
export function measureLabel(m: CatalogueMeasure | undefined, lang: 'en' | 'ar'): string | null {
  const raw = m?.label?.[lang];
  if (!raw) return null;
  return lang === 'en'
    ? raw.replace(/\{people\}/g, 'people').replace(/\{People\}/g, 'People')
    : raw.replace(/\{people\}|\{People\}/g, 'السكان');
}
